import { type ReactNode, useState } from 'react'
import { Head, Link, router, useForm, usePage } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import SuggestCombobox from '@/components/admin/research/SuggestCombobox'
import { US_STATES } from '@/lib/usStates'
import { Archive, Pencil, Trash2 } from 'lucide-react'

type Venue = {
  id: string
  name: string
  city: string | null
  state: string | null
  address: string | null
  zip: string | null
  notes: string | null
  events_count: number
  organizations_count: number
}

type Props = {
  venues: Venue[]
  filters: { archived: string }
}

export default function ResearchVenuesIndex({ venues, filters }: Props) {
  const [editing, setEditing] = useState<Venue | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Venue | null>(null)
  const archivedView = Boolean(filters.archived)
  const pageErrors = usePage().props.errors as Record<string, string | string[]> | undefined

  const { data, setData, patch, processing } = useForm({
    research_venue: { name: '', city: '', state: '', address: '', zip: '', notes: '' },
  })

  const form = data.research_venue

  function setField<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setData('research_venue', { ...form, [key]: value })
  }

  function openEdit(venue: Venue) {
    setData('research_venue', {
      name: venue.name,
      city: venue.city ?? '',
      state: venue.state ?? '',
      address: venue.address ?? '',
      zip: venue.zip ?? '',
      notes: venue.notes ?? '',
    })
    setEditing(venue)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editing) return
    patch(`/admin/research/venues/${editing.id}`, { onSuccess: () => setEditing(null) })
  }

  function confirmDelete() {
    if (!deleteTarget) return
    router.delete(`/admin/research/venues/${deleteTarget.id}`)
    setDeleteTarget(null)
  }

  const nameError = pageErrors?.name
  const nameErrorMessage = Array.isArray(nameError) ? nameError[0] : nameError

  return (
    <>
      <Head title="Research - Venues" />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-muted-foreground">
            Sorted by most events hosted. Click a venue to see the organizations and dates behind
            the numbers. Venues are created automatically when you type a new venue name on an
            event.
          </p>
          <div className="flex shrink-0 gap-1">
            <Button
              variant={!archivedView ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => router.get('/admin/research/venues', {}, { preserveState: true })}
            >
              Active
            </Button>
            <Button
              variant={archivedView ? 'secondary' : 'outline'}
              size="sm"
              onClick={() =>
                router.get('/admin/research/venues', { archived: '1' }, { preserveState: true })
              }
            >
              Archived
            </Button>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Address</TableHead>
                <TableHead className="text-center">Events</TableHead>
                <TableHead className="text-center">Organizations</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {venues.map(venue => (
                <TableRow key={venue.id}>
                  <TableCell className="font-medium">
                    <Link href={`/admin/research/venues/${venue.id}`} className="hover:underline">
                      {venue.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {[venue.city, venue.state].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {[venue.address, venue.zip].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell className="text-center">{venue.events_count}</TableCell>
                  <TableCell className="text-center">{venue.organizations_count}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {archivedView ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              router.patch(`/admin/research/venues/${venue.id}/restore`)
                            }
                          >
                            Restore
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8 text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteTarget(venue)}
                            title="Delete permanently"
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            onClick={() => openEdit(venue)}
                            title="Edit"
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8 text-muted-foreground"
                            onClick={() =>
                              router.patch(`/admin/research/venues/${venue.id}/archive`)
                            }
                            title="Archive"
                          >
                            <Archive className="size-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {venues.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    {archivedView ? 'No archived venues.' : 'No venues yet.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={open => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit venue</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="venue-name">
                Name<span className="text-destructive"> *</span>
              </Label>
              <Input
                id="venue-name"
                value={form.name}
                onChange={e => setField('name', e.target.value)}
              />
              {nameErrorMessage && <p className="text-xs text-destructive">{nameErrorMessage}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="venue-city">City</Label>
                <Input
                  id="venue-city"
                  value={form.city}
                  onChange={e => setField('city', e.target.value)}
                />
              </div>
              <SuggestCombobox
                label="State"
                id="venue-state"
                suggestions={US_STATES}
                value={form.state}
                onChange={value => setField('state', value)}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="venue-address">Address</Label>
                <Input
                  id="venue-address"
                  value={form.address}
                  onChange={e => setField('address', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="venue-zip">Zip Code</Label>
                <Input
                  id="venue-zip"
                  value={form.zip}
                  onChange={e => setField('zip', e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="venue-notes">Notes</Label>
              <Textarea
                id="venue-notes"
                rows={3}
                value={form.notes}
                onChange={e => setField('notes', e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permanently delete venue</DialogTitle>
            <DialogDescription>
              Permanently delete &ldquo;{deleteTarget?.name}&rdquo;? Events using it keep their
              other details but lose the venue link. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

ResearchVenuesIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
